// Sign-in methods on the website (owner decision 2026-10-19): Discord, GitHub, passkeys, and handing a sign-in to
// the Banger app. accounts does the checking (backend/accounts/src/oauth.ts, passkeys.ts); this keeps the
// browser's side of each flow: the secret that binds an OAuth round trip to this tab, the WebAuthn calls, and the
// app's PKCE challenge.

import { api } from "../api.ts";
import { adoptSession, type SessionReply, type Verified } from "../session.ts";

export type Provider = "discord" | "github";
export const PROVIDER_NAME: Record<Provider, string> = { discord: "Discord", github: "GitHub" };

export interface Methods {
  email: boolean;
  discord: boolean;
  github: boolean;
  passkey: boolean;
  /** dev only: the providers are accounts' fake */
  fake?: boolean;
}

let methodsP: Promise<Methods> | null = null;
/** What this deployment offers (providers only once the owner configured them). Older accounts: email only. */
export function getMethods(): Promise<Methods> {
  methodsP ??= api<Methods>("accounts", "/auth/methods", { auth: false }).catch(() => ({ email: true, discord: false, github: false, passkey: false }));
  return methodsP;
}

// ---- base64url and PKCE --------------------------------------------------------------------------------------

export function b64url(bytes: ArrayBuffer | Uint8Array): string {
  const b = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let s = "";
  for (const x of b) s += String.fromCharCode(x);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function fromB64url(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
  return Uint8Array.from(bin, (c) => c.charCodeAt(0));
}

export const randomVerifier = () => b64url(crypto.getRandomValues(new Uint8Array(32)));
export async function s256(v: string): Promise<string> {
  return b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(v)));
}

/** An S256 challenge as the app sends it (`cc=`): 43 base64url characters. */
export const isChallenge = (s: string | null): s is string => !!s && /^[A-Za-z0-9_-]{43}$/.test(s);

// ---- Discord and GitHub ------------------------------------------------------------------------------------------

const FLOW_KEY = "thrdm.oauth";

function storage(): Storage | null {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
}

/**
 * Starts a sign-in (or, with `link`, linking to the signed-in account): keeps a secret in this tab, sends accounts
 * its hash, and goes to the provider. The provider sends the browser back with a code that only this tab can finish.
 */
export async function startProvider(provider: Provider, opts: { next?: string; link?: boolean } = {}): Promise<void> {
  const verifier = randomVerifier();
  const st = storage();
  if (!st) throw new Error("This browser blocks the storage sign-in needs. Allow site data for banger.sh, or sign in with email.");
  st.setItem(FLOW_KEY, JSON.stringify({ verifier, provider, at: Date.now() }));
  const r = await api<{ url: string }>("accounts", `/auth/oauth/${provider}/start`, { body: { binder: await s256(verifier), next: opts.next, link: opts.link || undefined }, auth: !!opts.link });
  location.assign(r.url);
}

function takeVerifier(): { verifier: string; provider: Provider } | null {
  const st = storage();
  try {
    const v = JSON.parse(st?.getItem(FLOW_KEY) ?? "null") as { verifier?: string; provider?: Provider; at?: number } | null;
    if (!v?.verifier || !v.provider || Date.now() - (v.at ?? 0) > 15 * 60_000) return null;
    return { verifier: v.verifier, provider: v.provider };
  } catch {
    return null;
  }
}

export const forgetFlow = () => storage()?.removeItem(FLOW_KEY);

export type Finished = (Verified & { kind: "signed_in"; provider: Provider; next: string | null }) | { kind: "linked"; provider: Provider; next: string | null };

/** Finishes the round trip with the code the provider's redirect brought back (and the age a new player typed). */
export async function finishProvider(code: string, age?: number): Promise<Finished> {
  const flow = takeVerifier();
  if (!flow) throw Object.assign(new Error("This sign-in was started in another tab or has expired. Please start again."), { code: "code_invalid" });
  const r = await api<SessionReply & { provider: Provider; next: string | null; linked?: Provider }>("accounts", "/auth/oauth/finish", { body: { code, verifier: flow.verifier, age } });
  forgetFlow();
  if (r.linked) return { kind: "linked", provider: r.linked, next: r.next };
  return { ...adoptSession(r), kind: "signed_in", provider: r.provider, next: r.next };
}

// ---- passkeys --------------------------------------------------------------------------------------------------

export const passkeysSupported = (): boolean => typeof window !== "undefined" && typeof window.PublicKeyCredential === "function" && !!navigator.credentials;

export async function conditionalSupported(): Promise<boolean> {
  try {
    return passkeysSupported() && typeof PublicKeyCredential.isConditionalMediationAvailable === "function" && (await PublicKeyCredential.isConditionalMediationAvailable());
  } catch {
    return false;
  }
}

const cred = (c: PublicKeyCredential) => {
  const r = c.response as AuthenticatorAttestationResponse & AuthenticatorAssertionResponse;
  return {
    id: c.id,
    rawId: b64url(c.rawId),
    type: c.type,
    response: {
      clientDataJSON: b64url(r.clientDataJSON),
      ...(r.attestationObject ? { attestationObject: b64url(r.attestationObject) } : {}),
      ...(r.authenticatorData ? { authenticatorData: b64url(r.authenticatorData), signature: b64url(r.signature), userHandle: r.userHandle ? b64url(r.userHandle) : null } : {}),
    },
  };
};

/**
 * Signs in with a passkey. `conditional`: offered in the email field's autofill (one WebAuthn request at a time, so
 * callers abort it before starting another). Null when the person cancelled.
 */
export async function passkeySignIn(opts: { conditional?: boolean; signal?: AbortSignal } = {}): Promise<Verified | null> {
  const o = await api<{ challengeId: string; publicKey: { challenge: string; rpId: string; timeout: number; userVerification: UserVerificationRequirement } }>("accounts", "/auth/passkey/login/options", { body: {}, auth: false });
  let c: PublicKeyCredential | null;
  try {
    c = (await navigator.credentials.get({
      mediation: opts.conditional ? ("conditional" as CredentialMediationRequirement) : undefined,
      signal: opts.signal,
      publicKey: { challenge: fromB64url(o.publicKey.challenge), rpId: o.publicKey.rpId, timeout: o.publicKey.timeout, userVerification: o.publicKey.userVerification, allowCredentials: [] },
    })) as PublicKeyCredential | null;
  } catch (e) {
    if (e instanceof DOMException && (e.name === "NotAllowedError" || e.name === "AbortError")) return null;
    throw e;
  }
  if (!c) return null;
  return adoptSession(await api<SessionReply>("accounts", "/auth/passkey/login", { body: { challengeId: o.challengeId, credential: cred(c) }, auth: false }));
}

export interface Passkey {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
}

/** Adds a passkey to the signed-in account. Null when the person cancelled. */
export async function addPasskey(name: string): Promise<Passkey | null> {
  type Opts = {
    challengeId: string;
    publicKey: {
      challenge: string;
      rp: PublicKeyCredentialRpEntity;
      user: { id: string; name: string; displayName: string };
      pubKeyCredParams: PublicKeyCredentialParameters[];
      excludeCredentials: { type: "public-key"; id: string }[];
      authenticatorSelection: AuthenticatorSelectionCriteria;
      attestation: AttestationConveyancePreference;
      timeout: number;
    };
  };
  const o = await api<Opts>("accounts", "/auth/passkey/register/options", { body: {} });
  let c: PublicKeyCredential | null;
  try {
    c = (await navigator.credentials.create({
      publicKey: {
        ...o.publicKey,
        challenge: fromB64url(o.publicKey.challenge),
        user: { ...o.publicKey.user, id: fromB64url(o.publicKey.user.id) },
        excludeCredentials: o.publicKey.excludeCredentials.map((x) => ({ type: x.type, id: fromB64url(x.id) })),
      },
    })) as PublicKeyCredential | null;
  } catch (e) {
    if (e instanceof DOMException && e.name === "NotAllowedError") return null;
    if (e instanceof DOMException && e.name === "InvalidStateError") throw new Error("This device already has a passkey for your account.");
    throw e;
  }
  if (!c) return null;
  return (await api<{ passkey: Passkey }>("accounts", "/auth/passkey/register", { body: { challengeId: o.challengeId, name: name || undefined, credential: cred(c) } })).passkey;
}

/** A name for a new passkey from this device ("Mac", "iPhone"…). */
export function deviceName(ua = typeof navigator !== "undefined" ? navigator.userAgent : ""): string {
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android phone";
  if (/Mac OS X|Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows PC";
  if (/Linux/.test(ua)) return "Linux computer";
  return "This device";
}

// ---- the app ---------------------------------------------------------------------------------------------------

/** A one-time code for the Banger app that asked (`cc`, its PKCE challenge): the thrdm:// link that hands it over. */
export async function appHandoffLink(challenge: string): Promise<string> {
  return (await api<{ link: string }>("accounts", "/auth/handoff", { body: { challenge } })).link;
}

export interface SigninMethods {
  email: string | null;
  identities: { provider: Provider; email: string | null; createdAt: number }[];
  passkeys: Passkey[];
  available: Methods;
}
export const getSigninMethods = () => api<SigninMethods>("accounts", "/me/signin-methods");
